import type { RegistryKind, RegistryNameConflict } from "./types";

/**
 * TUT-171 — a identidade da ficha de perito e de advogado, do lado do navegador.
 *
 * **Esta é uma segunda cópia da regra, e a duplicação é deliberada** — o mesmo
 * preço que `normalizeSubjectLabel` já paga aqui: o modo demo não fala com a
 * API, então sem uma cópia a tela pareceria certa em demonstração e erraria
 * contra o servidor. O que não se pode é DIVERGIR: a fonte é
 * `apps/api/src/domain/registry-forms.ts` e
 * `apps/api/src/domain/registry-identity.ts`, e as mensagens são as mesmas
 * palavra por palavra, porque é por elas que a e2e distingue um caminho do
 * outro.
 *
 * O que a tela usa daqui além do demo: o RÓTULO do campo de documento, que
 * troca de "CPF" para "CNPJ" quando a ficha é marcada como empresa. Sem isso o
 * campo pediria "documento" e a recusa por 11 dígitos só chegaria depois de
 * salvar.
 */

export interface FichaDeCadastro {
  id: string;
  name: string;
  kind: RegistryKind;
  document: string | null;
  firmId: string | null;
}

export interface RotulosDeCadastro {
  /** "empresa de perícia" / "escritório" */
  empresa: string;
  /** "perito" / "advogado" */
  ficha: string;
  /** TUT-289 — a frase de rebaixamento (empresa com vinculados virando
   * pessoa) diverge de `ficha`/`empresa` no backend, palavra por palavra:
   * "Esta empresa"/"cadastro(s) vinculado(s)"/"ela" para perito,
   * "Este escritório"/"advogado(s) vinculado(s)"/"ele" para advogado. */
  rebaixamento: {
    sujeito: string;
    vinculadoSingular: string;
    vinculadoPlural: string;
    pronome: string;
  };
}

export const ROTULOS_PERITO: RotulosDeCadastro = {
  empresa: "empresa de perícia",
  // TUT-289: o backend (`apps/api/src/experts/experts.service.ts`) já fala
  // genérico — "uma pessoa", não "um perito" — porque o registro passou a
  // guardar os dois tipos. A cópia daqui segue a mesma palavra por palavra.
  ficha: "uma pessoa",
  rebaixamento: {
    sujeito: "Esta empresa",
    vinculadoSingular: "cadastro vinculado",
    vinculadoPlural: "cadastros vinculados",
    pronome: "ela",
  },
};

export const ROTULOS_ADVOGADO: RotulosDeCadastro = {
  empresa: "escritório",
  ficha: "advogado",
  rebaixamento: {
    sujeito: "Este escritório",
    vinculadoSingular: "advogado vinculado",
    vinculadoPlural: "advogados vinculados",
    pronome: "ele",
  },
};

function digitosDoDocumento(value: string | null | undefined): string {
  return (value ?? "").replace(/\D/g, "");
}

/** Ver `apps/api/src/domain/registry-identity.ts`: maiúsculas, sem separadores. */
export function normalizarOab(value: string | null | undefined): string | null {
  const limpo = (value ?? "").replace(/[^0-9A-Za-z]/g, "").toUpperCase();
  return limpo.length > 0 ? limpo : null;
}

/**
 * TUT-536 — o registro do perito do juízo, normalizado como a OAB: maiúsculas
 * sem separadores (o conselho/UF digitado faz parte da identidade). Não exige
 * o conselho: `5069123456` sozinho é aceito. Só o registro vazio depois de
 * normalizar (só símbolos) é recusado, pela API (`registroConferido`).
 * Espelha `normalizarRegistro` da API e a coluna `registration_normalized`.
 */
export const normalizarRegistro = normalizarOab;

function isValidCpf(value: string): boolean {
  const digits = digitosDoDocumento(value);
  if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) return false;
  const digito = (ate: number): number => {
    let soma = 0;
    for (let i = 0; i < ate; i += 1) soma += Number(digits[i]) * (ate + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return digito(9) === Number(digits[9]) && digito(10) === Number(digits[10]);
}

function digitoCnpj(base: string, pesos: number[]): number {
  const soma = [...base].reduce(
    (total, atual, indice) => total + Number(atual) * (pesos[indice] ?? 0),
    0,
  );
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

export function isValidCnpj(value: string): boolean {
  const digits = digitosDoDocumento(value);
  if (digits.length !== 14 || /^(\d)\1{13}$/.test(digits)) return false;
  const primeiro = digitoCnpj(
    digits.slice(0, 12),
    [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2],
  );
  const segundo = digitoCnpj(
    `${digits.slice(0, 12)}${primeiro}`,
    [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2],
  );
  return digits.endsWith(`${primeiro}${segundo}`);
}

/**
 * TUT-589 — o CPF ou CNPJ com a máscara, para a tela. A API pode devolver só
 * os dígitos; com outro tamanho o texto volta como veio (não inventa máscara).
 */
export function formatarDocumento(value: string | null | undefined): string {
  const digits = digitosDoDocumento(value);
  if (digits.length === 11) {
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/u, "$1.$2.$3-$4");
  }
  if (digits.length === 14) {
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/u, "$1.$2.$3/$4-$5");
  }
  return (value ?? "").trim();
}

/** O rótulo do campo de documento, que depende do tipo da ficha. */
export function rotuloDoDocumento(kind: RegistryKind): string {
  return kind === "COMPANY" ? "CNPJ" : "CPF";
}

/** O motivo pelo qual o documento não serve, ou `null` se ele serve. */
export function motivoDeDocumentoInvalido(
  kind: RegistryKind,
  bruto: string | null | undefined,
): string | null {
  const texto = (bruto ?? "").trim();
  if (texto.length === 0) return null;
  const digits = digitosDoDocumento(texto);
  if (kind === "COMPANY") {
    if (digits.length !== 14) {
      return "A ficha está marcada como empresa, então o documento é o CNPJ: informe 14 dígitos.";
    }
    return isValidCnpj(digits)
      ? null
      : "CNPJ inválido: o dígito verificador não confere. Um dígito trocado cria a segunda ficha da mesma empresa.";
  }
  if (digits.length !== 11) {
    return "A ficha é de uma pessoa, então o documento é o CPF: informe 11 dígitos. Se for uma empresa, marque a opção acima.";
  }
  return isValidCpf(digits)
    ? null
    : "CPF inválido: o dígito verificador não confere. Um dígito trocado cria a segunda ficha da mesma pessoa.";
}

/**
 * A frase do aviso de homônimo, ou vazio quando não há.
 *
 * Devolve string vazia em vez de `null` porque é isso que `OperationFeedback`
 * entende como "não mostre nada" — e concentrar a decisão aqui evita que cada
 * tela invente a própria frase para o mesmo fato.
 */
export function avisoDeHomonimo(
  conflito: RegistryNameConflict | null,
  rotulo: string,
): string {
  if (!conflito) return "";
  return `Salvo. Já existe outro ${rotulo} chamado «${conflito.name}» — o nome deixou de ser único porque quem identifica a ficha é o documento. Confira se não foi engano.`;
}

/** O homônimo que já existe — AVISO, nunca recusa. */
export function homonimoDemo<T extends FichaDeCadastro>(
  name: string,
  fichas: T[],
  exceptId?: string,
): RegistryNameConflict | null {
  const outro = fichas.find(
    (ficha) => ficha.id !== exceptId && ficha.name === name,
  );
  return outro ? { id: outro.id, name: outro.name } : null;
}

/** A colisão que RECUSA — comparada por dígito, como a coluna gerada do banco. */
export function documentoOcupadoDemo<T extends FichaDeCadastro>(
  document: string | null,
  fichas: T[],
  exceptId?: string,
): boolean {
  const digits = digitosDoDocumento(document);
  if (!digits) return false;
  return fichas.some(
    (ficha) =>
      ficha.id !== exceptId && digitosDoDocumento(ficha.document) === digits,
  );
}

/** Ver `motivoDeVinculoInvalido` na API — a mesma regra, as mesmas frases. */
export function vinculoDemoInvalido<T extends FichaDeCadastro>(
  kind: RegistryKind,
  firmId: string | null,
  fichas: T[],
  rotulos: RotulosDeCadastro,
): string | null {
  if (!firmId) return null;
  if (kind === "COMPANY") {
    return `Uma ficha de ${rotulos.empresa} não pode ser vinculada a outra: o vínculo tem um nível só.`;
  }
  const empresa = fichas.find((ficha) => ficha.id === firmId);
  if (!empresa) {
    return `A ${rotulos.empresa} escolhida não existe neste escritório.`;
  }
  if (empresa.kind !== "COMPANY") {
    return `A ficha escolhida é de uma pessoa, não de ${rotulos.empresa}. Só uma ficha marcada como empresa recebe vinculados.`;
  }
  return null;
}

/**
 * A empresa com gente vinculada não pode virar pessoa.
 *
 * É a regra que NENHUM `CHECK` do banco alcança: ele olha a linha de quem TEM
 * empresa, nunca a de quem É empresa.
 */
export function rebaixamentoDemoInvalido<T extends FichaDeCadastro>(
  atual: T,
  proximoKind: RegistryKind,
  fichas: T[],
  rotulos: RotulosDeCadastro,
): string | null {
  if (atual.kind !== "COMPANY" || proximoKind !== "PERSON") return null;
  const vinculados = fichas.filter((ficha) => ficha.firmId === atual.id).length;
  if (vinculados === 0) return null;
  const { sujeito, vinculadoSingular, vinculadoPlural, pronome } =
    rotulos.rebaixamento;
  const plural = vinculados === 1 ? vinculadoSingular : vinculadoPlural;
  return `${sujeito} tem ${vinculados} ${plural} e não pode virar uma ficha de pessoa. Desvincule quem responde por ${pronome} antes.`;
}

/**
 * O nome da empresa e a conta de vinculados, como o servidor os resolve. Só os
 * ATIVOS contam (TUT-479, D9): o número é o total da lista que o clique abre, e
 * ela não traz os desativados.
 */
export function decorarFichaDemo<
  T extends FichaDeCadastro & { firmName: string | null; memberCount: number; active: boolean },
>(ficha: T, fichas: T[]): T {
  return {
    ...ficha,
    firmName: ficha.firmId
      ? (fichas.find((item) => item.id === ficha.firmId)?.name ?? null)
      : null,
    memberCount:
      ficha.kind === "COMPANY"
        ? fichas.filter((item) => item.firmId === ficha.id && item.active).length
        : 0,
  };
}
